import type { Category, ProductCategory } from '@scayle/storefront-nuxt'
import { useRouteHelpers } from '.'
import type { BreadcrumbItem } from '~/types/breadcrumbs'
import { getCategoryAncestors } from '#storefront-product-listing'
import { useCurrentShop } from '#storefront/composables'
import { useRoute } from '#app/composables/router'
import { useGenderMenuItems } from '~/composables/useGenderMenuItems'
import { useNuxtApp } from '#app'
import { useTopNavigationMenu } from '~/composables/useTopNavigationMenu'
import { normalizeWithoutSpecialCharacters } from '~/utils/stringNormalize'

export function useBreadcrumbs() {
  const { buildCategoryPath, buildSingleBrandPath } = useRouteHelpers()
  const shopLocale = useCurrentShop()
  const { $i18n } = useNuxtApp()
  const { t } = $i18n

  const { path } = useRoute()
  const { isValidGender } = useGenderMenuItems()
  const { currentMenu } = useTopNavigationMenu()

  const getBreadcrumbsFromProductCategories = (
    categories: ProductCategory[],
  ): BreadcrumbItem[] => {
    return categories.map((category) => ({
      value: category.categoryName,
      to: {
        '@type': 'Thing',
        '@id': buildCategoryPath({
          id: category.categoryId,
          path: category.categoryUrl,
        }),
      },
    }))
  }

  const getBreadcrumb = (category: Category) => {
    const brandTranslation = t('brands_page.title').toLowerCase() // Get translated "brands" word
    const gender = currentMenu.value.toLowerCase()
    const isBrandListing =
      path.includes('/b/') ||
      category?.slug === brandTranslation ||
      category?.parent?.slug === brandTranslation

    function buildPath(category: Category) {
      return isBrandListing
        ? buildSingleBrandPath(category)
        : buildCategoryPath(category)
    }

    let link = buildPath(category)
    const formatedCategoryName = normalizeWithoutSpecialCharacters(
      category.name.toLowerCase(),
    )

    // if the category is "brands", we need to set the brands listing page path
    if (formatedCategoryName === brandTranslation) {
      link = `/${shopLocale.value?.path}/s/${gender}-${brandTranslation}`
    }

    return {
      to: {
        '@type': 'Thing',
        '@id': isValidGender(formatedCategoryName) // if the category is a gender(Ex.: Women), we need to set the gender page path
          ? `/${shopLocale.value?.path}/${formatedCategoryName}`
          : link,
      },
      value: category.name,
    }
  }

  const getBreadcrumbsFromCategory = (
    category: Category,
    includeActiveCategory = false,
  ): BreadcrumbItem[] => {
    const items = getCategoryAncestors(category).map(getBreadcrumb)
    return !includeActiveCategory ? items : [...items, getBreadcrumb(category)]
  }

  return {
    getBreadcrumb,
    getBreadcrumbsFromCategory,
    getBreadcrumbsFromProductCategories,
  }
}
